/**
 * @fileoverview The ten canonical category destinations as the city global nav
 * renders them (WEB-1180 / DES-108): the header Categories disclosure and the
 * ≤720 sitemap chip group read this one list so the two can never drift.
 *
 * Order, labels and icons come from `CATEGORIES` (`CATEGORY_ORDER` +
 * `CATEGORY_PRESENTATION`, WEB-606). Hrefs are host-relative `/category/<slug>`
 * — the city is the subdomain, never a path segment (see `defaultCategoryHref`).
 * There is deliberately no "All categories" entry: `/categories` does not exist.
 */
import type { LucideIcon } from 'lucide-react';

import type { CategorySlug } from '@/config/categories';
import type { CategoryTonightCounts } from '@/hooks/useCategoryTonightCounts';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';
import { formatCount } from '@/lib/format/count';
import { CATEGORIES } from '@/utils/categoryConstants';

import { defaultCategoryHref } from './categoryHref';

export interface CategoryNavEntry {
  readonly slug: CategorySlug;
  readonly label: string;
  readonly href: string;
  readonly Icon: LucideIcon;
  /** Tonight count, or `null` while counts are unknown (loading / failed). */
  readonly count: number | null;
  /** True only for a known zero — the dimmed `Soon` state. Never hidden. */
  readonly dim: boolean;
  /** Visible count cell: `24`, `Soon`, or `null` when unknown. */
  readonly countText: string | null;
  /** Accessible name carrying the count context, e.g. "Music, 24 tonight". */
  readonly ariaLabel: string;
}

/** Copy for a known-zero category (DES-108: dimmed, never hidden). */
export const CATEGORY_ZERO_TONIGHT_LABEL = 'Soon';

export function buildCategoryNavEntries(
  counts: CategoryTonightCounts | null,
): readonly CategoryNavEntry[] {
  return CATEGORIES.map((category) => {
    const slug = category.slug as CategorySlug;
    const label = categoryDisplayLabel(category.name);
    const count = counts ? counts[slug] : null;
    const countText =
      count === null ? null : count > 0 ? formatCount(count) : CATEGORY_ZERO_TONIGHT_LABEL;
    const ariaLabel =
      count === null
        ? label
        : count > 0
          ? `${label}, ${formatCount(count)} tonight`
          : `${label}, nothing tonight yet`;
    return {
      slug,
      label,
      href: defaultCategoryHref(slug),
      Icon: category.icon,
      count,
      dim: count === 0,
      countText,
      ariaLabel,
    };
  });
}

/** True on a `/category/<slug>` page — the Categories trigger is then current. */
export function isCategoryPath(pathname: string | null | undefined): boolean {
  return typeof pathname === 'string' && pathname.startsWith('/category/');
}

/** The active slug on `/category/<slug>[/…]`, else `null`. */
export function activeCategorySlug(pathname: string | null | undefined): string | null {
  if (!isCategoryPath(pathname)) return null;
  const slug = (pathname as string).slice('/category/'.length).split(/[/?#]/)[0];
  return slug ? slug : null;
}
